import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Box, Grid, Typography, Avatar, IconButton, CircularProgress, TextField, FormControl, InputLabel, Select, MenuItem, Button, Snackbar, Alert } from '@mui/material';
import { ArrowUpward } from '@mui/icons-material';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';
import { useTheme } from '@mui/material/styles';
import UserFeedbacks from '../User/UserFeedbacks';
import YouTubeEmbed from '../UserProfile/YouTubeEmbed';
import { LinkedIn} from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';




const API_URL = process.env.REACT_APP_API_URL;

const RequestUserTabDetails = ({ onLogout, userId }) => {

    useEffect(() => {
        window.scrollTo(0, 0);  // Scroll to top when this page is loaded
      }, []);

      
    const { id } = useParams();
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [languages, setLanguages] = useState([]);
    const [sections, setSections] = useState([]); // Sections data
    const [professions, setProfessions] = useState([]); // Professions data
    const [loading, setLoading] = useState(true);
    const [topic, setTopic] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [language, setLanguage] = useState('English');
    const [price, setPrice] = useState(0);
    const [duration, setDuration] = useState('');
    const [description, setDescription] = useState('');
    const [selectedSection, setSelectedSection] = useState(''); // Selected section ID
    const [selectedProfession, setSelectedProfession] = useState(''); // Selected profession ID
    const [openSnackbar, setOpenSnackbar] = useState(false); // Snackbar open state
    const [snackbarMessage, setSnackbarMessage] = useState(''); // Snackbar message
    const [buttonDisabled, setButtonDisabled] = useState(false); // State to disable the button after click
    const theme = useTheme();
    const [requestSuccess, setRequestSuccess] = useState(false);
    const [openRequestConfirmDialog, setOpenRequestConfirmDialog] = useState(false);




    const [isVerified, setIsVerified] = useState(false);
      
        useEffect(() => {
          const checkVerification = async () => {
            if (!userId) return;
      
            try {
              const response = await axios.post(`${API_URL}/api/check-verification/`, {
                user_id: userId,
              });
      
              setIsVerified(response.data.status === 'approved');
            } catch (error) {
              console.error('Verification check failed:', error);
              setIsVerified(false);
            } finally {
              setLoading(false);
            }
          };
      
          checkVerification();
        }, [userId]);


    

    useEffect(() => {
        axios.get(`${API_URL}/get_user_data/`, { params: { user_id: id } })
            .then(response => {
                setUserData(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error(error);
                setLoading(false);
            });

        axios.get(`${API_URL}/get_languages_and_professions/`, { params: { user_id: id } })
            .then(response => {
                setLanguages(response.data.languages);
            })
            .catch(error => {
                console.error(error);
            });

        axios.get(`${API_URL}/get_sections_and_professions/`)
            .then(response => {
                setSections(response.data); // Set the sections data
            })
            .catch(error => {
                console.error('Error fetching sections and professions:', error);
            });
    }, [id]);

    const handleLogout = () => {
        onLogout();
        navigate('/login');
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSectionChange = (e) => {
        const sectionId = e.target.value;
        setSelectedSection(sectionId);
        const selectedSection = sections.find(section => section.section_id === sectionId);
        if (selectedSection) {
            setProfessions(selectedSection.professions || []);
        }
    };

    const handleSubmit = () => {
        const selectedSectionObj = sections.find(section => section.section_id === selectedSection);
        const sectionName = selectedSectionObj ? selectedSectionObj.section_name : '';

        const selectedProfessionObj = professions.find(profession => profession.id === selectedProfession);
        const professionName = selectedProfessionObj ? selectedProfessionObj.profession_name : '';

        const requestData = {
            topic,
            date,
            time,
            language,
            price,
            duration,
            description, // Include description in the request data
            teacher_id: id,  // Teacher ID (from useParams)
            student_id: userId,  // Student ID (passed as a prop)
            section_name: sectionName, // Send section name
            subsection_name: professionName, // Send profession name
        };

        // Send POST request to the backend
        axios.post(`${API_URL}/create_request_user/`, requestData)
            .then(response => {
                setSnackbarMessage('Your request has been created successfully!');
                setOpenSnackbar(true);
                setButtonDisabled(true);  // Disable the button after successful submission
                setRequestSuccess(true);
            })
            .catch(error => {
                console.error('Error creating request:', error);
                setSnackbarMessage('There was an error creating your request. Please try again.');
                setOpenSnackbar(true);
                setButtonDisabled(false);  // Re-enable the button in case of error
                setRequestSuccess(false);
            });
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    return (
        <div>
            <Box sx={{ backgroundColor: "#000000", mt: 10 }}>
                <Header1 userId={userId} onLogout={onLogout} />

                <Typography
                    variant="h3"
                    component="h1"
                    align="center"
                    fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
                    sx={{
                        backgroundImage: 'linear-gradient(45deg, #ff00ff, #00bfff)',
                        mt: 10,
                        letterSpacing: 7,
                        WebkitBackgroundClip: 'text',
                        color: 'transparent',
                    }}
                    gutterBottom
                >
                    Request  {userData?.name ? `${userData.name} ${userData.surname}` : 'No name available'} for a Session
                </Typography>

                <Box sx={{ padding: 4 }}>
                    <IconButton onClick={scrollToTop} sx={{ position: 'fixed', bottom: 16, right: 16, backgroundColor: 'white', color: 'black' }}>
                        <ArrowUpward />
                    </IconButton>

                    {loading ? (
                        <CircularProgress sx={{ display: 'block', margin: 'auto', paddingTop: '50px' }} />
                    ) : (
                        <Grid container spacing={4}>
                            {/* User Profile Section */}
                            <Grid item xs={12} md={4}>
                                <Box sx={{ padding: 3, textAlign: 'center', boxShadow: 3, backgroundColor: '#2222' }}>
                               
                                    <Typography
                    variant="h3"
                    align="center"
                    fontSize={{ xs: '1rem', sm: '1.5rem', md: '2rem' }}
                    sx={{
                        backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
                        // mt: 10,
                        letterSpacing: 5,
                        WebkitBackgroundClip: 'text',
                        color: 'transparent',
                    }}
                    gutterBottom
                >
                    User Details
                </Typography>
                                    <Avatar
                                        alt="User Image"
                                        src={userData?.image ? `${API_URL}${userData.image}` : '/default-avatar.png'}
                                        sx={{ width: 150, height: 150, margin: 'auto' }}
                                    />
                                    <Box sx={{
                                                                        display: 'flex',
                                                                        justifyContent: 'center',
                                                                        alignItems: 'center',
                                                                        mt: 2, // Margin top for some space between the image and the icons
                                                                    }}>
                                    {userData?.linkedin && (
                                                                            <a href={userData?.linkedin} target="_blank" rel="noopener noreferrer">
                                                                                <IconButton sx={{
                                                                                    color: 'white',
                                                                                    "&:hover": {
                                                                                        transform: "translateY(-3px)",
                                                                                        color: '#1E90FF',
                                                                                    },
                                                                                    mx: 1,
                                                                                }}>
                                                                                    <LinkedIn />
                                                                                </IconButton>
                                                                            </a>
                                                                        )}</Box>
                                    <Link to={`/public-view-user/${id}`} style={{ textDecoration: "none" }}>
                                        <Typography variant="h5" sx={{ marginTop: 2, color:'white',"&:hover": { color: '#1E90FF' }, letterSpacing: 4, }}>
                                            {userData?.name ? `${userData.name} ${userData.surname}` : 'No name available'}
                                        </Typography>
                                    </Link>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#444",  // Lighter color for the helper text
                                        fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },  // Responsive font size
                                        marginTop: '0.5rem',
                                        letterSpacing: 2,
                                        marginBottom:'1rem',
                                        textAlign: 'center',  // Center the helper text
                                        fontStyle: 'italic',  // Optional: add some style to make it distinguishable
                                      }}
                                    >
                                      Click on user name to view more info about the user. {/* Customize this text */}
                                    </Typography>
                                   
                                                        {userData?.facebook && (
                                                       
                                                       <Typography variant="body2" sx={{ marginTop: '1rem', color: '#b0bec5', fontSize: { xs: '0.9rem', sm: '1rem' }, letterSpacing: 3,}}>
                                                         Current Role:  {userData?.facebook || 'No description available'}
                                                        </Typography>
                                                        )}


                                                       
                                    {userData?.youtube && (
                                                  <div>
                                                    <YouTubeEmbed
                                                      videoUrl={userData?.youtube}
                                                    />
                                                  </div>
                                                )}

<Typography variant="body2" sx={{ marginTop: 1,fontSize: { xs: '0.9rem', sm: '1rem' }, // Responsive font size
        fontStyle: 'italic',letterSpacing: 2, }}>
                                        {userData?.about || 'No bio available.'}
                                    </Typography>

                                    {userData?.Experience && (<Box sx={{ width: '100%', marginBottom: 3 }}>
  <Typography sx={{ color: 'white', marginTop: 2, fontWeight: 'bold', letterSpacing: 3, }}>
    Time of Availability
  </Typography>

  {userData?.Experience && (
    <Typography
      variant="body2"
      sx={{
        marginTop: '0.5rem',
        color: '#b0bec5',
        letterSpacing: 1,
        fontSize: { xs: '0.9rem', sm: '1rem' }, // Responsive font size
        fontStyle: 'italic', // Italicized for a different style
        background: 'rgba(255, 255, 255, 0)', // Light background for better contrast
        padding: '0.5rem 1rem', // Padding for spacing
        borderRadius: '8px', // Rounded corners for a modern look
      }}
    >
      {userData?.Experience || 'N/A'}
    </Typography>
  )}
</Box>)}

{userData?.languages && (<Box sx={{ width: '100%', marginBottom: 3 }}>
  <Typography sx={{ color: 'white', marginTop: 2, fontWeight: 'bold', letterSpacing: 3, }}>
    Languages Known
  </Typography>

  {userData?.languages && (
    <Typography
      variant="body2"
      sx={{
        marginTop: '0.5rem',
        color: '#b0bec5',
        letterSpacing: 1,
        fontSize: { xs: '0.9rem', sm: '1rem' }, // Responsive font size
        fontStyle: 'italic', // Italicized for emphasis
        background: 'rgba(255, 255, 255, 0)', // Light background for contrast
        padding: '0.5rem 1rem', // Padding for spacing
        borderRadius: '8px', // Rounded corners for modern look
      }}
    >
      {userData?.languages || 'N/A'}
    </Typography>
  )}
</Box>)}

{userData?.areas_of_interest && (<Box sx={{ width: '100%', marginBottom: 3 }}>
  <Typography sx={{ color: 'white', marginTop: 2, fontWeight: 'bold', letterSpacing: 3, }}>
    Areas of Interest
  </Typography>

  {userData?.areas_of_interest && (
    <Typography
      variant="body2"
      sx={{
        marginTop: '0.5rem',
        color: '#b0bec5',
        fontSize: { xs: '0.9rem', sm: '1rem' }, // Responsive font size
        fontStyle: 'italic', // Italicized for emphasis
        background: 'rgba(255, 255, 255, 0)', // Light background for contrast
        padding: '0.5rem 1rem', // Padding for spacing
        borderRadius: '8px', // Rounded corners for modern look
        letterSpacing: 1,
      }}
    >
      {userData?.areas_of_interest && userData?.areas_of_interest.length > 0
        ? userData?.areas_of_interest.join(', ') // If it's a list, display them as comma-separated values
        : 'N/A'}
    </Typography>
  )}
</Box>)}

                                </Box>
                               
                               
                            </Grid>

                            <Grid item xs={12} md={8}>
                                <Box sx={{ padding: 3, boxShadow: 3, backgroundColor: '#2222' }}>
                                   
                                    
                                    <Typography
                    variant="h3"
                    align="center"
                    fontSize={{ xs: '1rem', sm: '1.5rem', md: '2rem' }}
                    sx={{
                        backgroundImage: 'linear-gradient(45deg, #ff00ff, #00bfff)',
                        // mt: 10,
                        letterSpacing: 5,
                        WebkitBackgroundClip: 'text',
                        color: 'transparent',
                    }}
                    gutterBottom
                >
                    Request Details
                </Typography>

                                    <FormControl fullWidth sx={{ marginBottom: 2, letterSpacing: 2, }}>
                                        <InputLabel>Category</InputLabel>
                                        <Select
                                            value={selectedSection}
                                            onChange={handleSectionChange}
                                            label="Category"
                                        >
                                            {sections.map(section => (
                                                <MenuItem key={section.section_id} value={section.section_id} sx={{letterSpacing: 2}}>
                                                    {section.section_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth sx={{ marginBottom: 2, letterSpacing: 2 }}>
                                        <InputLabel>Subcategory</InputLabel>
                                        <Select
                                            value={selectedProfession}
                                            onChange={e => setSelectedProfession(e.target.value)}
                                            label="Subcategory"
                                            disabled={!selectedSection}
                                        >
                                            {professions.map(profession => (
                                                <MenuItem key={profession.id} value={profession.id} sx={{letterSpacing: 2}}>
                                                    {profession.profession_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
  label="Title"
  variant="outlined"
  fullWidth
  value={topic}
  onChange={e => {
    if (e.target.value.length <= 100) {
      setTopic(e.target.value);
    }
  }}
  sx={{ marginBottom: 2, letterSpacing: 2 }}
  inputProps={{ maxLength: 100 }}
  helperText={`${topic.length}/100 characters`}
  FormHelperTextProps={{
    style: {
      letterSpacing: 2,
      color: topic.length > 90 ? 'orange' : 'inherit', // Warning color if over 90 characters
    },
  }}
/>


                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Date"
                                                type="date"
                                                fullWidth
                                                value={date}
                                                onChange={e => setDate(e.target.value)}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                sx={{ marginBottom: 2, letterSpacing: 2 }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Time"
                                                type="time"
                                                fullWidth
                                                value={time}
                                                onChange={e => setTime(e.target.value)}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                sx={{ marginBottom: 2, letterSpacing: 2 }}
                                            />
                                        </Grid>
                                    </Grid>

                                    

                                    <FormControl fullWidth sx={{ marginBottom: 2, letterSpacing: 2 }}>
                                        <InputLabel>Language</InputLabel>
                                        <Select
                                            value={language}
                                            onChange={e => setLanguage(e.target.value)}
                                            label="Language"
                                        >
                                            {languages.map(lang => (
                                                <MenuItem key={lang.id} value={lang.name} sx={{letterSpacing: 2}}>{lang.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>


                                    <FormControl fullWidth sx={{ marginBottom: 2, letterSpacing: 2 }}>
                                        <InputLabel>Duration (in Minutes)</InputLabel>
                                        <Select
                                            label="Duration (in Minutes)"
                                            value={duration}
                                            sx={{letterSpacing: 2}}
                                            onChange={e => setDuration(e.target.value)}
                                        >
                                            {[...Array(19)].map((_, index) => {
                                                const value = 30 + index * 15;
                                                return (
                                                    <MenuItem key={value} value={value} sx={{letterSpacing: 2}}>
                                                        {value} minutes
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>

                                    <TextField
  label="Description"
  variant="outlined"
  fullWidth
  multiline
  rows={4}
  value={description}
  onChange={e => {
    if (e.target.value.length <= 500) {
      setDescription(e.target.value);
    }
  }}
  sx={{ marginBottom: 2, letterSpacing: 2 }}
  inputProps={{ maxLength: 500 }}
  helperText={`${description.length}/500 characters`}
  FormHelperTextProps={{
    style: {
      letterSpacing: 2,
      color: description.length > 450 ? 'orange' : 'inherit', // Change color if over 450
    },
  }}
/>


                                    {!buttonDisabled && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            disabled={!isVerified || loading}
                                            sx={{letterSpacing: 2}}
                                            onClick={() => setOpenRequestConfirmDialog(true)}
                                        >
                                            Request Now
                                        </Button>
                                    )}

                                    {!loading && !isVerified && (
                                            <Typography
                                              variant="body2"
                                              color="#1E90FF"
                                              sx={{ mt: 2, letterSpacing: 4, textAlign:'center' }}
                                            >
                                              Only verified users are allowed to request for a session
                                            </Typography>
                                          )}

{requestSuccess && (
  <Box
    sx={{
      textAlign: "center",
      mt: 5,
      px: 3,
      py: 4,
      borderRadius: "12px",
      backgroundColor: "#001F3F",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
    }}
  >
    <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 40, mb: 2 }} />

    <Typography
      variant="h6"
      sx={{
        color: "#1E90FF",
        letterSpacing: 2,
        fontWeight: 700,
        fontSize: {
          xs: "1.1rem",
          sm: "1.3rem",
          md: "1.5rem",
        },
        lineHeight: 1.6,
        mb: 1,
      }}
    >
      Request made Successfully!
    </Typography>

    <Typography
      variant="body1"
      sx={{
        color: "#b0c4de",
        letterSpacing: 1.5,
        fontSize: {
          xs: "0.9rem",
          sm: "1rem",
          md: "1.1rem",
        },
        maxWidth: "600px",
        margin: "0 auto",
      }}
    >
      Please visit <strong>My Sync</strong>  <strong>/</strong> <strong>User Sessions</strong>  <strong>/</strong>  <strong>Requests Sent to Users</strong> to view your updates.
    </Typography>
  </Box>
)}

                                </Box>
                            </Grid>
                            <Dialog
  open={openRequestConfirmDialog}
  onClose={() => setOpenRequestConfirmDialog(false)}
  PaperProps={{
    sx: {
      borderRadius: "16px",
      padding: 2,
      backgroundColor: "#000000", // dark background
      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.6)",
      maxWidth: 420,
      mx: 'auto',
    },
  }}
  transitionDuration={300}
>
  <DialogTitle
    sx={{
      fontWeight: 'bold',
      fontSize: '1.5rem',
      textAlign: 'center',
      color: '#1E90FF',
      pb: 1,
      letterSpacing: 1.5,
    }}
  >
    Confirm Request
  </DialogTitle>

  <DialogContent sx={{ textAlign: 'center', px: 3 }}>
    <Typography sx={{ fontSize: '1rem', color: '#ccc', letterSpacing: 1.5, }}>
      Are you sure you want to request this session?
    </Typography>
  </DialogContent>

  <DialogActions sx={{ justifyContent: 'center', gap: 2, mt: 2 }}>
    <Button
      onClick={() => setOpenRequestConfirmDialog(false)}
      sx={{
        borderRadius: '25px',
        padding: '0.6rem 1.5rem',
        backgroundColor: '#333',
        color: '#ddd',
        textTransform: 'none',
        letterSpacing: 1.5,
        "&:hover": {
          backgroundColor: '#444',
        },
      }}
    >
      Cancel
    </Button>

    <Button
      onClick={() => {
        handleSubmit(); // Submit the request
        setOpenRequestConfirmDialog(false); // Close dialog
      }}
      variant="contained"
      sx={{
        borderRadius: '25px',
        padding: '0.6rem 1.8rem',
        textTransform: 'none',
        background: "linear-gradient(45deg, #1e90ff, #1c70ff)",
        fontWeight: 'bold',
        letterSpacing: 1,
        color: '#fff',
        letterSpacing: 1.5,
        "&:hover": {
          transform: 'scale(1.05)',
          boxShadow: '0 8px 20px rgba(30, 144, 255, 0.4)',
        },
      }}
    >
      Confirm
    </Button>
  </DialogActions>
</Dialog>

                            
                        </Grid>
                    )}
                </Box>
                <UserFeedbacks userId={id}/>
            </Box>
            <Footer2 />
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%', letterSpacing: 2 }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default RequestUserTabDetails;
